import {
  addAsset,
  addressErrorPopup,
  addressResponseType,
  getNearestAddress,
  initialCenterWCC,
  markerIcon,
  onMapClick,
  createAssetFeatureLayer,
  getCustodianCodes,
  checkExternalPublicSpacesAndDisplayPopup,
} from '@utils';
import L, {
  LatLngTuple,
  LayerGroup,
  Marker,
  Popup,
  LeafletMouseEvent,
  esri,
  LeafletKeyboardEvent,
} from 'leaflet';
import { useEffect, useRef, useState } from 'react';
import { MapContainer, useMap } from 'react-leaflet';
import { SearchControl } from './SearchControl/SearchControl';
import { EsriTileLayer } from './EsriTileLayer/EsriTileLayer';
import 'leaflet/dist/leaflet.css';
import { AssetConfig, AssetType, AssetTypeConfig } from '@types';
import * as Sentry from '@sentry/react';
import styles from './MapComponent.module.css';
import { orderByDistance, getDistance } from 'geolib';

interface MapComponentProps {
  defaultLocation?: LatLngTuple;
  onChange: (location: addressResponseType) => void;
  selectedAddress?: addressResponseType;
  customAddressContent?: string | React.ReactNode;
  assetType?: AssetTypeConfig | AssetTypeConfig[];
  enableBuffer?: boolean;
  displayMapAssets?: boolean;
  enableTfLBoundaryCheck?: boolean;
  minZoomToDisplayAssets?: {
    targetAssetType: AssetType;
    minZoom: number;
  };
}

const MapContent = ({
  onChange,
  defaultLocation,
  assetType,
  enableBuffer,
  displayMapAssets = false,
  enableTfLBoundaryCheck,
  selectedAddress,
  minZoomToDisplayAssets,
}: Pick<
  MapComponentProps,
  | 'onChange'
  | 'defaultLocation'
  | 'assetType'
  | 'enableBuffer'
  | 'displayMapAssets'
  | 'enableTfLBoundaryCheck'
  | 'selectedAddress'
  | 'minZoomToDisplayAssets'
>) => {
  const map = useMap();
  const graphicsLayerRef = useRef<LayerGroup | null>(null);
  const selectedAssetLayerRef = useRef<LayerGroup | null>(null);
  const assetLayerRef = useRef<esri.FeatureLayer[] | null>(null);
  const [searchTerm, setSearchTerm] = useState(
    selectedAddress?.full_address ?? '',
  );
  const [displayAssets, setDisplayAssets] = useState(displayMapAssets);
  const { targetAssetType, minZoom } = minZoomToDisplayAssets ?? {};
  let isTargetAssetType = false;
  if (targetAssetType && assetType) {
    if (Array.isArray(assetType)) {
      isTargetAssetType =
        assetType
          .map((asset) => (asset as AssetConfig).asset)
          .includes(targetAssetType) || assetType.includes(targetAssetType);
    } else {
      isTargetAssetType = assetType === targetAssetType;
    }
  }

  const clearGraphics = () => {
    if (graphicsLayerRef.current) {
      graphicsLayerRef.current.clearLayers();
    }
  };

  const clearSelectedAsset = () => {
    if (selectedAssetLayerRef.current) {
      selectedAssetLayerRef.current.clearLayers();
    }
  };

  const drawSelectedAsset = (feature: any) => {
    if (!feature || !selectedAssetLayerRef.current) return;
    selectedAssetLayerRef.current.clearLayers();

    if (feature.geometry.type === 'Point') {
      selectedAssetLayerRef.current.addLayer(feature);
    } else {
      const highlightStyle = {
        color: '#88f6f7',
        weight: 2,
        opacity: 0.9,
        fillColor: '#fbfbcc',
      } as L.PathOptions;

      const layer = L.geoJSON(feature as any, {
        style: () => highlightStyle,
        pointToLayer: (_geoJsonPoint, latlng) =>
          L.circleMarker(latlng, {
            radius: 10,
            color: highlightStyle.color,
            weight: highlightStyle.weight,
            opacity: highlightStyle.opacity,
            fillOpacity: highlightStyle.fillOpacity,
          }),
      });
      selectedAssetLayerRef.current.addLayer(layer);
    }
  };

  // for Trees assets, only display them after user has zoomed in
  useEffect(() => {
    if (targetAssetType && minZoom !== undefined) {
      if (isTargetAssetType && map.getZoom() < minZoom) {
        setDisplayAssets(false);
      }
      map.on('zoomend', () => {
        if (isTargetAssetType && map.getZoom() < minZoom) {
          setDisplayAssets(false);
        } else {
          setDisplayAssets(displayMapAssets);
        }
      });

      return () => {
        map.off('zoomend');
      };
    }
  }, [isTargetAssetType, targetAssetType, minZoom, map]);

  useEffect(() => {
    graphicsLayerRef.current = new LayerGroup();
    map.addLayer(graphicsLayerRef.current);
    selectedAssetLayerRef.current = new LayerGroup();
    map.addLayer(selectedAssetLayerRef.current);

    if (displayAssets && assetType) {
      const handleMapEvent = async (
        e: LeafletMouseEvent | LeafletKeyboardEvent,
      ) => {
        e.originalEvent.preventDefault();
        const feature = e.propagatedFrom.feature;
        let lat;
        let lng;

        if (e.type === 'click') {
          lat = (e as LeafletMouseEvent).latlng.lat;
          lng = (e as LeafletMouseEvent).latlng.lng;
        } else {
          // Handles keyboard event - latlng property is not available on keyboard event
          lat = feature.geometry.coordinates[1];
          lng = feature.geometry.coordinates[0];
        }

        let addressFromPin = await getNearestAddress(
          lat,
          lng,
          getCustodianCodes(assetTypes, enableBuffer),
        );

        if (addressFromPin === null) {
          if ('latlng' in e) {
            addressErrorPopup(map, e);
          } else {
            addressErrorPopup(map);
          }
          return;
        }

        // check for Royal parks and other park owners for Trees asset type
        if (targetAssetType === 'Trees' || targetAssetType === 'PublicSpaces') {
          const isExternalPark = await checkExternalPublicSpacesAndDisplayPopup(
            addressFromPin,
            map,
            (e as LeafletMouseEvent).latlng,
          );
          if (isExternalPark) {
            return;
          }
        }

        if (feature && feature.properties) {
          const location: addressResponseType = {
            ...addressFromPin,
            latitude: lat,
            longitude: lng,
            asset: feature.properties,
          };
          clearGraphics();
          clearSelectedAsset();
          onChange(location);
          setSearchTerm(location.full_address);
          if (!targetAssetType) {
            map.flyTo([lat, lng]);
          }
          addMarker([lat, lng]);
          try {
            drawSelectedAsset(feature);
          } catch (err) {}
        }
      };

      assetLayerRef.current = [];
      // Adds all the assets to the map
      const assetTypes = Array.isArray(assetType) ? assetType : [assetType];
      assetTypes.forEach((asset) => {
        const assetLayer = createAssetFeatureLayer(asset);

        assetLayer.on('keydown', async (e) => {
          if (e.originalEvent.key === 'Enter' || e.originalEvent.key === ' ') {
            await handleMapEvent(e);
            const nextButton = document.querySelector(
              '[data-testid="button-next"]',
            );
            if (nextButton instanceof HTMLElement) {
              nextButton.focus();
            }
          }
        });

        // click event handler for asset selection and map update
        assetLayer.on('click', async (e) => {
          await handleMapEvent(e);
        });

        assetLayerRef.current!.push(assetLayer);
        map.addLayer(assetLayer);
        if (defaultLocation) {
          addMarker(defaultLocation);
        }
      });
    }
    return () => {
      if (graphicsLayerRef.current) {
        map.removeLayer(graphicsLayerRef.current);
      }
      if (selectedAssetLayerRef.current) {
        map.removeLayer(selectedAssetLayerRef.current);
      }
      if (assetLayerRef.current) {
        assetLayerRef.current.forEach((layer) => {
          map.removeLayer(layer);
        });
      }
    };
  }, [map, displayAssets]);

  const addMarker = (latlng: LatLngTuple, popupContent?: string) => {
    if (graphicsLayerRef.current) {
      graphicsLayerRef.current.clearLayers();
    }

    const marker = new Marker(latlng, {
      icon: markerIcon,
      zIndexOffset: 2,
      keyboard: false, // Disable keyboard interaction for markers
    });

    // Remove data-testid from any existing markers
    document
      .querySelectorAll('.leaflet-marker-icon[data-testid="selected-address"]')
      .forEach((existing) => {
        existing.removeAttribute('data-testid');
      });
    // Attach data-testid to selected address marker
    marker.on('add', function () {
      const el = marker.getElement();
      if (el) {
        el.setAttribute('data-testid', 'selected-address');
      }
    });

    if (popupContent) {
      const popup = new Popup({
        autoClose: true,
        autoPan: true,
        autoPanPadding: [50, 50],
      }).setContent(popupContent);

      marker.bindPopup(popup);
    }

    if (graphicsLayerRef.current !== null) {
      graphicsLayerRef.current.addLayer(marker);
      if (popupContent) {
        marker.openPopup();
      }
    }
  };

  const mapMethods = {
    clearGraphics,
    addMarker,
    onChange,
    map,
    assetType,
    graphicsLayer: graphicsLayerRef,
  };

  useEffect(() => {
    const setMapDefaultLocation = async () => {
      if (defaultLocation !== undefined && defaultLocation.length === 2) {
        try {
          const address = await getNearestAddress(
            defaultLocation[0],
            defaultLocation[1],
            getCustodianCodes(assetType, enableBuffer),
          );
          if (address === null) {
            addressErrorPopup(map);
            return;
          } else {
            map.setView(defaultLocation, 18);
            addMarker(defaultLocation);
            await addAsset(assetType, onChange, address);
            setSearchTerm(address.full_address);
          }
        } catch (error) {
          Sentry.captureException(error);
        }
      }
    };

    map.on('click', async (event: LeafletMouseEvent) => {
      const targetEl = event.originalEvent.target as HTMLElement;
      const classNamesArray = Array.from(targetEl.classList || []);
      // Needed to prevent the map from getting out of focus when user clicks on the input
      const isMapElement = classNamesArray.some(
        (className) =>
          className === 'leaflet-touch' || className === 'leaflet-interactive',
      );
      if (isMapElement) {
        clearSelectedAsset();
        await onMapClick(
          event,
          mapMethods,
          setSearchTerm,
          enableBuffer,
          enableTfLBoundaryCheck,
        );
      }
    });

    setMapDefaultLocation();
    return () => {
      map.off('click');
    };
  }, []);

  const currentFocusIndexRef = useRef(-1);
  const focusableElementsRef = useRef<HTMLElement[]>([]);

  useEffect(() => {
    if (!map || !selectedAddress) return;

    // Reset focus index when address changes
    currentFocusIndexRef.current = -1;

    // Function to set tabindex=-1 on all map elements except search controls
    const setTabIndexOnMapElements = () => {
      const mapElements = document.querySelectorAll(
        '.leaflet-marker-icon, .leaflet-interactive',
      );
      mapElements.forEach((el) => {
        if (el instanceof HTMLElement) {
          el.setAttribute('tabindex', '-1');
        }
      });
    };

    // Set tabindex on map elements
    setTabIndexOnMapElements();

    // Handle keydown events for navigation
    const handleKeyDown = (e: KeyboardEvent) => {
      // For Enter key on map elements
      if ((e.key === 'Enter' || e.key === ' ') && document.activeElement) {
        const active = document.activeElement;
        if (
          active.classList.contains('leaflet-marker-icon') ||
          active.classList.contains('leaflet-interactive')
        ) {
          e.preventDefault();
          // Cast to HTMLElement to access click() method
          try {
            (active as HTMLElement).click();
          } catch (error) {
            console.error('Error clicking element:', error);
          }
          return;
        }
      }

      // Handle arrow key navigation - ONLY if we have a valid selected address
      if (['ArrowDown', 'ArrowRight', 'ArrowUp', 'ArrowLeft'].includes(e.key)) {
        // Check if we have a valid selected address
        if (!selectedAddress?.latitude || !selectedAddress?.longitude) {
          return;
        }

        // Always force a complete re-sort on first arrow key press
        // This ensures we have the most up-to-date sorting
        const elements = document.querySelectorAll(
          '.leaflet-marker-icon, .leaflet-interactive',
        );

        // Filter elements to those with valid lat/lng attributes
        let arr = Array.from(elements).filter((el) => {
          if (!(el instanceof HTMLElement)) return false;
          const lat = parseFloat(el.getAttribute('data-lat') || 'NaN');
          const lng = parseFloat(el.getAttribute('data-lng') || 'NaN');
          return !isNaN(lat) && !isNaN(lng);
        }) as HTMLElement[];

        if (arr.length === 0) {
          return;
        }

        // Always sort by distance from selected address
        const points = arr.map((el) => ({
          latitude: parseFloat(el.getAttribute('data-lat') || '0'),
          longitude: parseFloat(el.getAttribute('data-lng') || '0'),
          element: el,
        }));

        try {
          // Use geolib's orderByDistance to sort points
          const sortedPoints = orderByDistance(
            {
              latitude: Number(selectedAddress.latitude),
              longitude: Number(selectedAddress.longitude),
            },
            points,
          );

          // Filter by radius (100 meters) and extract sorted elements
          const RADIUS_METERS = 100;
          const filteredPoints = sortedPoints.filter((point) => {
            const distance = getDistance(
              {
                latitude: Number(selectedAddress.latitude),
                longitude: Number(selectedAddress.longitude),
              },
              {
                latitude: (point as unknown as (typeof points)[0]).latitude,
                longitude: (point as unknown as (typeof points)[0]).longitude,
              },
            );
            return distance <= RADIUS_METERS;
          });

          // Extract filtered and sorted elements
          arr = filteredPoints.map(
            (point) => (point as unknown as (typeof points)[0]).element,
          );

          // Update the focusable elements with the freshly sorted array
          focusableElementsRef.current = arr;

          // Always reset to start from the beginning (nearest marker)
          // when first arrow key is pressed after address selection
          if (currentFocusIndexRef.current === -1) {
            currentFocusIndexRef.current = 0;
            try {
              focusableElementsRef.current[0].focus();
            } catch (error) {
              console.error('Error focusing first element:', error);
            }
            e.preventDefault();
            return;
          }

          // Navigate based on arrow key
          let newIndex;
          switch (e.key) {
            case 'ArrowDown':
            case 'ArrowRight':
              newIndex = (currentFocusIndexRef.current + 1) % arr.length;
              if (currentFocusIndexRef.current >= 0) {
                try {
                  focusableElementsRef.current[
                    currentFocusIndexRef.current
                  ].blur();
                } catch (error) {
                  console.error('Error blurring element:', error);
                }
              }
              currentFocusIndexRef.current = newIndex;
              try {
                focusableElementsRef.current[newIndex].focus();
              } catch (error) {
                console.error('Error focusing element:', error);
              }
              break;
            case 'ArrowUp':
            case 'ArrowLeft':
              newIndex =
                (currentFocusIndexRef.current - 1 + arr.length) % arr.length;
              if (currentFocusIndexRef.current >= 0) {
                try {
                  focusableElementsRef.current[
                    currentFocusIndexRef.current
                  ].blur();
                } catch (error) {
                  console.error('Error blurring element:', error);
                }
              }
              currentFocusIndexRef.current = newIndex;
              try {
                focusableElementsRef.current[newIndex].focus();
              } catch (error) {
                console.error('Error focusing element:', error);
              }
              break;
          }
        } catch (error) {
          console.error('Error during keyboard navigation:', error);
        }

        e.preventDefault();
      }
    };

    // Add event listener to the document
    document.addEventListener('keydown', handleKeyDown);

    // Clean up
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [map, selectedAddress]);

  useEffect(() => {
    if (!map) return;

    // Function to set tabindex=-1 on all map elements except search controls
    const disableTabForMapElements = () => {
      // Select all map elements
      const mapElements = document.querySelectorAll('.leaflet-container *');

      mapElements.forEach((element) => {
        if (element instanceof HTMLElement) {
          // Disable tab for all elements
          element.setAttribute('tabindex', '-1');
        }
      });

      // Enable tab for search input
      const searchInput = document.querySelector('#map-search-input');
      if (searchInput instanceof HTMLElement) {
        searchInput.setAttribute('tabindex', '0');
      }

      // Enable tab for search and clear buttons
      // Target the specific SVG buttons based on their aria labels
      const buttons = document.querySelectorAll(
        'button[aria-label="Clear search input"], button[aria-label="Search for address"]',
      );
      buttons.forEach((button) => {
        if (button instanceof HTMLElement) {
          button.setAttribute('tabindex', '0');
        }
      });
    };

    // Call once when component mounts
    disableTabForMapElements();

    // Create a mutation observer to handle dynamically added elements
    const observer = new MutationObserver(() => {
      disableTabForMapElements();
    });

    // Start observing the map container for changes
    const mapContainer = document.querySelector('.leaflet-container');
    if (mapContainer) {
      observer.observe(mapContainer, {
        childList: true,
        subtree: true,
      });
    }

    // Cleanup the observer when component unmounts
    return () => {
      observer.disconnect();
    };
  }, [map]); // Only re-run when map reference changes

  // Add accessibility attributes to map elements for better screen reader support
  useEffect(() => {
    if (!map) return;

    // Function to set ARIA attributes on map elements
    const setMapElementAccessibility = () => {
      // Add roles and labels to markers
      document
        .querySelectorAll('.leaflet-marker-icon')
        .forEach((marker, index) => {
          if (marker instanceof HTMLElement) {
            marker.setAttribute('role', 'button');
          }
        });

      // Add labels to zoom controls
      const zoomIn = document.querySelector('.leaflet-control-zoom-in');
      if (zoomIn instanceof HTMLElement) {
        zoomIn.setAttribute('aria-label', 'Zoom in');
      }

      const zoomOut = document.querySelector('.leaflet-control-zoom-out');
      if (zoomOut instanceof HTMLElement) {
        zoomOut.setAttribute('aria-label', 'Zoom out');
      }
    };

    // Run initially
    setMapElementAccessibility();

    // Setup observer to detect new elements
    const accessibilityObserver = new MutationObserver(
      setMapElementAccessibility,
    );
    // Cleanup observer on unmount
    const container = map.getContainer();

    accessibilityObserver.observe(container, {
      childList: true,
      subtree: true,
    });

    return () => {
      accessibilityObserver.disconnect();
    };
  }, [map]);

  return (
    <>
      <SearchControl
        mapMethods={mapMethods}
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
        enableBuffer={enableBuffer}
        enableTfLBoundaryCheck={enableTfLBoundaryCheck}
      />
      <div className={styles.mapContainer}>
        <EsriTileLayer enableBuffer={enableBuffer} />
      </div>
    </>
  );
};

export const MapComponent = ({
  defaultLocation,
  onChange,
  selectedAddress,
  customAddressContent,
  assetType,
  enableBuffer,
  displayMapAssets = false,
  enableTfLBoundaryCheck,
  minZoomToDisplayAssets,
}: MapComponentProps) => {
  const initialCenter = defaultLocation || initialCenterWCC;

  return (
    <>
      <div className={styles.mapContainer}>
        <MapContainer
          center={initialCenter}
          zoom={13}
          style={{ height: '70vh', position: 'relative' }}
          className="w-full"
        >
          <MapContent
            onChange={onChange}
            defaultLocation={defaultLocation}
            selectedAddress={selectedAddress}
            assetType={assetType}
            displayMapAssets={displayMapAssets}
            enableBuffer={enableBuffer}
            enableTfLBoundaryCheck={enableTfLBoundaryCheck}
            minZoomToDisplayAssets={minZoomToDisplayAssets}
          />
        </MapContainer>
      </div>

      {selectedAddress && selectedAddress.full_address && (
        <div className="mb-3" data-testid="map-display-address">
          {customAddressContent ? (
            customAddressContent
          ) : (
            <div className="flex flex-col gap-2">
              <p className="text-bodyLg font-semibold">You have selected</p>
              <p className="text-bodyLg font-normal">
                {selectedAddress.full_address}
              </p>
            </div>
          )}
        </div>
      )}
    </>
  );
};
